import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { todayGate } from "@/lib/gates";
import { track } from "@/lib/analytics";

/**
 * Daily Gate of Consciousness — a quiet, meditative frame that surfaces one of
 * the 47 Gates each day. Deterministic by day-of-year, so returning visitors
 * find a new Gate over time. Deep-links into the existing 47 Gates book page.
 */
export function DailyGate() {
  const gate = todayGate();
  const viewed = useRef(false);

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    track("daily_gate_view", { n: gate.n });
  }, [gate.n]);

  return (
    <section
      aria-labelledby="daily-gate-heading"
      className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24"
    >
      <div className="relative overflow-hidden rounded-3xl border hairline bg-obsidian/60 p-8 backdrop-blur sm:p-12">
        <div
          aria-hidden
          className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-gold/10 blur-3xl"
        />
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">
          Today&rsquo;s Gate · No. {String(gate.n).padStart(2, "0")}
        </p>
        <h2
          id="daily-gate-heading"
          className="mt-4 font-serif text-3xl leading-tight text-gold-luminous sm:text-4xl"
        >
          {gate.title}
        </h2>
        <p className="mt-5 max-w-2xl font-serif text-base italic leading-relaxed text-ivory/85 sm:text-lg">
          &ldquo;{gate.reflection}&rdquo;
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/gates/$n"
            params={{ n: String(gate.n) }}
            onClick={() => track("daily_gate_click", { n: gate.n })}
            className="group inline-flex items-center gap-2 rounded-full border border-gold bg-gold/10 px-5 py-2.5 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
          >
            Enter this Gate
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/books/$slug"
            params={{ slug: "47-gates-of-consciousness" }}
            className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground hover:text-gold"
          >
            Study the book →
          </Link>
        </div>
      </div>
    </section>
  );
}
